import { OppButton } from '../ui/control';
import { Package } from 'lucide-react';
import { useCopy } from '../../copy';
import { SemanticState } from '../semantic/SemanticState';

interface CourierTaskItem {
  id: string;
  status: string;
  pickup_op_code?: string;
  delivery_op_code?: string;
  created_at: string;
}

interface OppCourierRecentTasksProps {
  tasks?: CourierTaskItem[];
  onCollect: (taskId: string) => void;
  onDepart: (taskId: string) => void;
  onDeliver: (taskId: string) => void;
  pendingTaskId?: string | null;
}

export function OppCourierRecentTasks({ tasks, onCollect, onDepart, onDeliver, pendingTaskId }: OppCourierRecentTasksProps) {
  const { t } = useCopy();

  return (
    <div>
      <h4 className="mb-2 text-sm font-semibold text-foreground">{t('courier.recent')}</h4>
      {tasks && tasks.length > 0 ? (
        <div className="space-y-1 overflow-hidden rounded-xl bg-card">
          {tasks.map((task) => {
            const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
              pending: { bg: 'bg-letter-amber-light', text: 'text-[hsl(var(--opp-ink))]', label: t('courier.task.pending') },
              collected: { bg: 'bg-feedback-info/15', text: 'text-feedback-info', label: t('courier.task.collected') },
              in_transit: { bg: 'bg-feedback-info/15', text: 'text-feedback-info', label: t('courier.task.in_transit') },
              delivered: { bg: 'bg-feedback-success/15', text: 'text-feedback-success', label: t('courier.task.delivered') },
              failed: { bg: 'bg-[hsl(var(--opp-danger-bg))]', text: 'text-[hsl(var(--opp-danger-fg))]', label: t('courier.task.failed') },
              cancelled: { bg: 'bg-muted', text: 'text-muted-foreground', label: t('courier.task.cancelled') },
            };
            const status = statusConfig[task.status] || statusConfig.pending;

            return (
              <div key={task.id} className="flex flex-wrap items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted/40">
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                  task.status === 'in_transit' || task.status === 'collected' ? 'bg-feedback-info/15' : 'bg-[hsl(var(--opp-accent-soft))]'
                }`}>
                  <Package className={`h-4 w-4 ${
                    task.status === 'in_transit' || task.status === 'collected' ? 'text-feedback-info' : 'text-[hsl(var(--opp-accent-strong))]'
                  }`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-[hsl(var(--opp-ink))]">
                    {task.pickup_op_code ?? '?'} → {task.delivery_op_code ?? '?'}
                  </p>
                  <p className="text-caption text-[hsl(var(--opp-muted))]">{new Date(task.created_at).toLocaleDateString()}</p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-caption font-semibold ${status.bg} ${status.text}`}>
                    {status.label}
                  </span>
                  {task.status === 'pending' && (
                    <OppButton
                      onClick={() => onCollect(task.id)}
                      disabled={Boolean(pendingTaskId)}
                      className="min-h-11 rounded-full bg-feedback-success/10 px-3 text-xs font-semibold text-feedback-success disabled:opacity-50"
                    >
                      {pendingTaskId === task.id ? '处理中…' : t('courier.task.action.collect')}
                    </OppButton>
                  )}
                  {task.status === 'collected' && (
                    <OppButton
                      onClick={() => onDepart(task.id)}
                      disabled={Boolean(pendingTaskId)}
                      className="min-h-11 rounded-full bg-feedback-info/10 px-3 text-xs font-semibold text-feedback-info disabled:opacity-50"
                    >
                      {pendingTaskId === task.id ? '处理中…' : t('courier.task.action.depart')}
                    </OppButton>
                  )}
                  {task.status === 'in_transit' && (
                    <OppButton
                      onClick={() => onDeliver(task.id)}
                      disabled={Boolean(pendingTaskId)}
                      className="min-h-11 rounded-full bg-feedback-success/10 px-3 text-xs font-semibold text-feedback-success disabled:opacity-50"
                    >
                      {pendingTaskId === task.id ? '处理中…' : t('courier.task.action.deliver')}
                    </OppButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <SemanticState compact illustration="empty-sent" kind="empty" title={t('courier.noTasks')} description="有新的投递任务时会显示在这里。" />
      )}
    </div>
  );
}
